/**
 * AI Insights Service
 *
 * Handles all API requests for the AI Intelligence Hub:
 * - Requirements Scorecard
 * - Risk Identification
 * - Test Case Generation
 * - Gap Analysis
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

/**
 * Scorecard types
 */
export interface ScorecardDimensionScores {
  completeness: number;
  clarity: number;
  testability: number;
  consistency: number;
}

export interface RequirementScore {
  requirement_id: string;
  requirement_title: string;
  scores: ScorecardDimensionScores;
  recommendations: string[];
}

export interface ScorecardData {
  id: string;
  generated_at: string;
  tokens_used: number;
  model: string;
  overall_score: number;
  dimension_averages: ScorecardDimensionScores;
  requirement_scores: RequirementScore[];
  summary: string;
}

/**
 * Risk types
 */
export interface Risk {
  title: string;
  description: string;
  probability: number;
  impact: number;
  risk_score: number;
  category: string;
  mitigation: string;
  related_requirements: string[];
}

export interface RiskMatrixSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface RiskData {
  id: string;
  generated_at: string;
  tokens_used: number;
  model: string;
  risks: Risk[];
  risk_matrix_summary: RiskMatrixSummary;
  summary: string;
}

/**
 * Test case types
 */
export interface TestCase {
  title: string;
  preconditions: string[];
  steps: string[];
  expected_result: string;
  test_type: 'functional' | 'edge_case' | 'negative';
}

export interface RequirementTestCases {
  requirement_id: string;
  requirement_title: string;
  tests: TestCase[];
}

export interface TestCaseSummary {
  total_test_cases: number;
  by_type: {
    functional: number;
    edge_case: number;
    negative: number;
  };
}

export interface TestCaseData {
  id: string;
  generated_at: string;
  tokens_used: number;
  model: string;
  test_cases: RequirementTestCases[];
  summary: TestCaseSummary;
}

/**
 * Gap analysis types
 */
export interface Gap {
  category: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  missing_areas: string[];
  recommendations: string[];
  rationale: string;
  converted_to_requirement_id?: string;
}

export interface GapAnalysisData {
  id: string;
  generated_at: string;
  tokens_used: number;
  model: string;
  gaps: Gap[];
  coverage_score: number;
  coverage_by_category: Record<string, number>;
  comparison_note: string;
  summary: string;
}

/**
 * All insights summary
 */
export interface InsightSummary {
  id: string;
  generated_at: string;
  tokens_used: number;
  model: string;
}

export interface AllInsightsData {
  projectId: string;
  projectName: string;
  insights: {
    scorecard: InsightSummary | null;
    risks: InsightSummary | null;
    test_cases: InsightSummary | null;
    gap_analysis: InsightSummary | null;
  };
}

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  details?: string;
}

/**
 * Get all insights summary for a project
 */
export async function getAllInsights(projectId: string, token: string): Promise<AllInsightsData> {
  const response = await axios.get<ApiResponse<AllInsightsData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Generate requirements scorecard
 */
export async function generateScorecard(projectId: string, token: string): Promise<ScorecardData> {
  const response = await axios.post<ApiResponse<ScorecardData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/scorecard`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Get latest scorecard
 */
export async function getScorecard(projectId: string, token: string): Promise<ScorecardData> {
  const response = await axios.get<ApiResponse<ScorecardData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/scorecard`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Identify project risks
 */
export async function identifyRisks(projectId: string, token: string): Promise<RiskData> {
  const response = await axios.post<ApiResponse<RiskData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/risks`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Get latest risk analysis
 */
export async function getRisks(projectId: string, token: string): Promise<RiskData> {
  const response = await axios.get<ApiResponse<RiskData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/risks`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Generate test cases
 */
export async function generateTestCases(projectId: string, token: string): Promise<TestCaseData> {
  const response = await axios.post<ApiResponse<TestCaseData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/test-cases`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Get latest test cases
 */
export async function getTestCases(projectId: string, token: string): Promise<TestCaseData> {
  const response = await axios.get<ApiResponse<TestCaseData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/test-cases`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Analyze requirements gaps
 */
export async function analyzeGaps(projectId: string, token: string): Promise<GapAnalysisData> {
  const response = await axios.post<ApiResponse<GapAnalysisData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/gap-analysis`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Get latest gap analysis
 */
export async function getGapAnalysis(projectId: string, token: string): Promise<GapAnalysisData> {
  const response = await axios.get<ApiResponse<GapAnalysisData>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/gap-analysis`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Create requirement from gap analysis item
 */
export interface CreateRequirementFromGapResponse {
  requirement_id: string;
  requirement_code: string;
  title: string;
  gap_index: number;
}

export async function createRequirementFromGap(
  projectId: string,
  gapIndex: number,
  token: string
): Promise<CreateRequirementFromGapResponse> {
  const response = await axios.post<ApiResponse<CreateRequirementFromGapResponse>>(
    `${API_BASE_URL}/projects/${projectId}/ai-insights/gap-analysis/create-requirement`,
    { gap_index: gapIndex },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Export test cases to CSV format
 */
export function exportTestCasesToCSV(testCaseData: TestCaseData): string {
  const headers = ['Requirement ID', 'Requirement Title', 'Test Title', 'Type', 'Preconditions', 'Steps', 'Expected Result'];
  const rows: string[][] = [headers];

  testCaseData.test_cases.forEach(reqTestCases => {
    reqTestCases.tests.forEach(test => {
      rows.push([
        reqTestCases.requirement_id,
        `"${reqTestCases.requirement_title.replace(/"/g, '""')}"`,
        `"${test.title.replace(/"/g, '""')}"`,
        test.test_type,
        `"${test.preconditions.join('; ').replace(/"/g, '""')}"`,
        `"${test.steps.join('; ').replace(/"/g, '""')}"`,
        `"${test.expected_result.replace(/"/g, '""')}"`
      ]);
    });
  });

  return rows.map(row => row.join(',')).join('\n');
}

/**
 * Export test cases to JSON format
 */
export function exportTestCasesToJSON(testCaseData: TestCaseData): string {
  return JSON.stringify(testCaseData, null, 2);
}

/**
 * Download string as file
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const aiInsightsService = {
  getAllInsights,
  generateScorecard,
  getScorecard,
  identifyRisks,
  getRisks,
  generateTestCases,
  getTestCases,
  analyzeGaps,
  getGapAnalysis,
  createRequirementFromGap,
  exportTestCasesToCSV,
  exportTestCasesToJSON,
  downloadFile
};

export default aiInsightsService;
